import { useMemo, useState } from 'react';
import { useScroll } from 'react-use';

import useRect from '@lyearn/core/hooks/useRect';
import { useTranslation } from '@lyearn/i18n';
import { cn } from '@lyearn/style';
import { Stack } from '@lyearn/ui';

import DialogContent from '../DialogContent';
import DialogFooter from '../DialogFooter';
import DialogFullWidthFooter from '../DialogFullWidthFooter';
import DialogHeader from '../DialogHeader';
import { DialogBodyProps } from './types';

const getDomValue = (value?: number) => value ?? 0;

const DialogBody = (props: DialogBodyProps) => {
  const { t } = useTranslation();
  const {
    children,
    contentClassName,
    header,
    primaryAction,
    secondaryAction: secondaryActionProp,
    styles,
    onClose,
  } = props;

  const [ref, setRef] = useState<HTMLDivElement | null>(null);
  useRect({ ref });
  const { y } = useScroll({ current: ref });

  const secondaryAction = useMemo(() => {
    if (secondaryActionProp === 'hidden') {
      return undefined;
    }

    return {
      label: secondaryActionProp?.label ?? t('Common:Actions.Cancel'),
      onClick: onClose,
      ...secondaryActionProp,
    };
  }, [onClose, secondaryActionProp, t]);

  const isScrolled = y > 0;
  const hasYScroll = getDomValue(ref?.scrollHeight) > getDomValue(ref?.clientHeight);
  const Footer = styles?.footer?.variant === 'fullWidth' ? DialogFullWidthFooter : DialogFooter;

  return (
    <Stack className="h-full overflow-hidden" direction="col">
      <DialogHeader
        className={cn(styles?.header?.className, { 'border-b border-divider': isScrolled })}
        description={header.description}
        overrides={styles?.header?.overrides}
        title={header.title}
      />
      {children ? (
        <DialogContent ref={setRef} className={contentClassName}>
          {children}
        </DialogContent>
      ) : null}
      <Footer
        buttonSize={styles?.footer?.buttonSize}
        className={cn(styles?.footer?.className, { 'border-t border-divider': isScrolled })}
        gapX={styles?.footer?.gapX}
        primaryAction={primaryAction}
        secondaryAction={secondaryAction}
      />
    </Stack>
  );
};

export default DialogBody;
